import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  Mail,
  Phone,
  Lock,
  Unlock,
  Download,
  UserCheck,
  UserX,
  LogIn
} from 'lucide-react'
import useUserStore from '@/stores/userStore'
import { User, UserFilters } from '@/services/userService'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

// Auth method icons mapping
const authMethodIcons: Record<string, { icon: string; color: string; label: string }> = {
  password: { icon: '🔑', color: 'bg-gray-100 text-gray-700', label: 'Password' },
  google: { icon: 'G', color: 'bg-blue-100 text-blue-700', label: 'Google' },
  facebook: { icon: 'f', color: 'bg-indigo-100 text-indigo-700', label: 'Facebook' },
  apple: { icon: '🍎', color: 'bg-gray-900 text-white', label: 'Apple' },
  twitter: { icon: '𝕏', color: 'bg-gray-800 text-white', label: 'X/Twitter' },
  instagram: { icon: '📷', color: 'bg-pink-100 text-pink-700', label: 'Instagram' },
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  active: { label: 'Actif', color: 'text-green-800', bgColor: 'bg-green-100 border-green-200' },
  inactive: { label: 'Inactif', color: 'text-gray-800', bgColor: 'bg-gray-100 border-gray-200' },
  locked: { label: 'Verrouillé', color: 'text-red-800', bgColor: 'bg-red-100 border-red-200' },
  deleted: { label: 'Supprimé', color: 'text-gray-900', bgColor: 'bg-gray-200 border-gray-300' },
}

const UserList = () => {
  const {
    users,
    loading,
    error,
    pagination,
    fetchUsers,
    deleteUser,
    bulkOperation,
  } = useUserStore()

  const navigate = useNavigate()
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [showBulkToolbar, setShowBulkToolbar] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showImpersonateModal, setShowImpersonateModal] = useState(false)
  const [impersonateTarget, setImpersonateTarget] = useState<User | null>(null)
  const [impersonateReason, setImpersonateReason] = useState('')
  const [currentUser, setCurrentUser] = useState<any>(null)

  useEffect(() => {
    fetchUsers()
    // Get current user from localStorage
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch {}
    }
  }, [])

  useEffect(() => {
    setShowBulkToolbar(selectedUsers.length > 0)
  }, [selectedUsers])

  const handleDelete = async (id: string) => {
    const user = users.find(u => u.user_id === id)
    if (!user) return
    
    if (!user.is_active) {
      alert('Cet utilisateur est déjà désactivé. Pour le réactiver, utilisez la page de détails.')
      return
    }
    
    if (window.confirm('Êtes-vous sûr de vouloir désactiver cet utilisateur ?')) {
      try {
        await deleteUser(id)
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Erreur inconnue'
        alert(`Erreur lors de la désactivation : ${errorMessage}`)
        console.error('Error deleting user:', error)
      }
    }
  }

  const handleSearch = (value: string) => {
    fetchUsers({ search: value })
  }

  const handleFilterChange = (filters: any[]) => {
    const filterObj: UserFilters = {}
    filters.forEach(filter => {
      if (filter.key === 'role' && filter.value) {
        filterObj.role = filter.value
      } else if (filter.key === 'status' && filter.value !== 'all') {
        if (filter.value === 'active') filterObj.is_active = true
        else if (filter.value === 'inactive') filterObj.is_active = false
        else if (filter.value === 'locked') filterObj.is_locked = true
      } else if (filter.key === 'auth_method' && filter.value) {
        filterObj.auth_method = filter.value
      }
    })
    
    fetchUsers(filterObj)
  }

  const handlePageChange = (page: number) => {
    fetchUsers({ page })
  }

  const handlePageSizeChange = (newSize: number) => {
    fetchUsers({ limit: newSize })
  }

  const handleBulkAction = async (action: string) => {
    if (selectedUsers.length === 0) return
    
    const userIds = selectedUsers.map(u => u.user_id)
    const confirmMsg = `Êtes-vous sûr de vouloir ${action} ${selectedUsers.length} utilisateur(s) ?`
    if (!window.confirm(confirmMsg)) return

    try {
      await bulkOperation(userIds, action as any)
      setSelectedUsers([])
      fetchUsers()
    } catch (error: any) {
      alert(`Échec de l'opération groupée : ${error.message}`)
    }
  }

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const { userService } = await import('@/services/userService')
      const blob = await userService.exportUsers(format)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setShowExportModal(false)
    } catch (error: any) {
      alert(`Échec de l'exportation : ${error.message}`)
    }
  }

  const handleImpersonate = async () => {
    if (!impersonateTarget || !impersonateReason.trim()) return
    try {
      const { userService } = await import('@/services/userService')
      const result = await userService.impersonateUser(
        currentUser?.userId || 'admin',
        impersonateTarget.user_id,
        impersonateReason
      )
      localStorage.setItem('impersonation_token', result.token)
      window.open('/', '_blank')
      setShowImpersonateModal(false)
      setImpersonateTarget(null)
      setImpersonateReason('')
    } catch (error: any) {
      alert(`Échec de l'usurpation d'identité : ${error.message}`)
    }
  }

  const getStatusBadge = (user: User) => {
    if (user.deleted_at) return statusConfig.deleted
    if (user.is_locked) return statusConfig.locked
    if (user.is_active) return statusConfig.active
    return statusConfig.inactive
  }

  // Get unique roles from users
  const roles = useMemo(() => {
    return Array.from(new Set(users.flatMap(u => u.roles || []))).sort()
  }, [users])

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (_value: any, row: User) => {
        if (!row) return <div>-</div>
        const firstName = row.first_name || (row.email ? row.email.split('@')[0] : 'Utilisateur')
        const lastName = row.last_name || '-'
        return (
          <div>
            <div className="font-medium text-gray-900">
              {firstName} {lastName !== '-' ? lastName : ''}
            </div>
            <div className="text-sm text-gray-500">
              {row.email}
            </div>
          </div>
        )
      }
    },
    {
      key: 'contact',
      label: 'Contact',
      sortable: true,
      render: (_value: any, row: User) => {
        if (!row) return <div>-</div>
        const phone = row.phone || 'N/D'
        return (
          <div>
            <div className="flex items-center text-sm text-gray-900">
              <Mail className="h-4 w-4 mr-2 text-gray-400" />
              {row.email}
            </div>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Phone className="h-4 w-4 mr-2 text-gray-400" />
              {phone}
            </div>
          </div>
        )
      }
    },
    {
      key: 'roles',
      label: 'Roles',
      sortable: true,
      render: (_value: any, row: User) => (
        <div className="flex flex-wrap gap-1">
          {(row.roles || []).map((role) => (
            <Badge
              key={role}
              variant={
                role === 'admin' ? 'destructive' :
                role === 'merchant' ? 'default' :
                'secondary'
              }
              className="text-xs"
            >
              {role}
            </Badge>
          ))}
        </div>
      )
    },
    {
      key: 'auth_method',
      label: 'Auth',
      sortable: true,
      render: (_value: any, row: User) => {
        const method = row.auth_method || 'password'
        const config = authMethodIcons[method]
        if (config) {
          return (
            <Badge variant="outline" className={`${config.color} text-xs`}>
              {config.icon} {config.label}
            </Badge>
          )
        }
        return <Badge variant="outline" className="text-xs">{method}</Badge>
      }
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (_value: any, row: User) => {
        const status = getStatusBadge(row)
        return (
          <Badge variant="outline" className={`${status.bgColor} ${status.color} border`}>
            {status.label}
          </Badge>
        )
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-500">
          {new Date(value).toLocaleDateString('fr-FR')}
        </div>
      )
    }
  ]

  // Define actions for DataTable
  const actions = [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: User) => navigate(`/users/${row.user_id}`),
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: User) => navigate(`/users/${row.user_id}/edit`),
      variant: 'outline' as const
    },
    {
      label: 'Deactivate',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: User) => handleDelete(row.user_id),
      variant: 'destructive' as const,
      disabled: (row: User) => !row.is_active
    },
    {
      label: 'Impersonate',
      icon: <LogIn className="h-4 w-4" />,
      onClick: (row: User) => {
        setImpersonateTarget(row)
        setShowImpersonateModal(true)
      },
      variant: 'default' as const,
    }
  ]

  // Define filter options
  const filterOptions = [
    {
      key: 'search',
      label: 'Rechercher',
      type: 'text' as const,
      placeholder: 'Name, email, phone...'
    },
    {
      key: 'role',
      label: 'Rôle',
      type: 'select' as const,
      options: [
        { value: '', label: 'Tous les rôles' },
        ...roles.map(role => ({
          value: role,
          label: role
        }))
      ]
    },
    {
      key: 'status',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'active', label: 'Actif' },
        { value: 'inactive', label: 'Inactif' },
        { value: 'locked', label: 'Verrouillé' },
        { value: 'deleted', label: 'Supprimé' }
      ]
    },
    {
      key: 'auth_method',
      label: "Méthode d'authentification",
      type: 'select' as const,
      options: [
        { value: '', label: 'Toutes les méthodes' },
        { value: 'password', label: 'Mot de passe' },
        { value: 'google', label: 'Google' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'apple', label: 'Apple' },
        { value: 'twitter', label: 'X/Twitter' },
        { value: 'instagram', label: 'Instagram' }
      ]
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Utilisateurs"
        subtitle="Gérez tous les utilisateurs de la plateforme"
        breadcrumbs={[
          { label: 'Tableau de bord', href: '/' },
          { label: 'Utilisateurs', href: '/users' }
        ]}
        actions={[
          {
            label: 'Nouvel utilisateur',
            icon: <Plus className="h-5 w-5" />,
            onClick: () => navigate('/users/new'),
            variant: 'default' as const
          },
          {
            label: 'Exporter',
            icon: <Download className="h-5 w-5" />,
            onClick: () => setShowExportModal(true),
            variant: 'outline' as const
          }
        ]}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher des utilisateurs..."
        onSearch={handleSearch}
        filters={filterOptions}
        onFilterChange={handleFilterChange}
      />

      {/* Bulk Action Toolbar */}
      {showBulkToolbar && (
        <Card className="bg-primary-50 border-primary-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-primary-800">
                  {selectedUsers.length} user(s) selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('activate')}
                >
                  <UserCheck className="h-4 w-4 mr-1" /> Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('deactivate')}
                >
                  <UserX className="h-4 w-4 mr-1" /> Deactivate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('lock')}
                >
                  <Lock className="h-4 w-4 mr-1" /> Lock
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('unlock')}
                >
                  <Unlock className="h-4 w-4 mr-1" /> Unlock
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkAction('delete')}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUsers([])}
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <DataTable
        data={users}
        columns={columns}
        actions={actions}
        loading={loading}
        selection={{
          selectedRows: selectedUsers,
          onSelectionChange: setSelectedUsers,
          getRowId: (row: User) => row.user_id
        }}
        pagination={{
          current: pagination.page,
          total: pagination.total,
          pageSize: pagination.limit,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucun utilisateur trouvé"
      />

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4">Export Users</h3>
              <p className="text-sm text-gray-600 mb-4">Choose export format:</p>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleExport('csv')}
                >
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleExport('json')}
                >
                  <Download className="h-4 w-4 mr-2" /> JSON
                </Button>
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" onClick={() => setShowExportModal(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Impersonate Modal */}
      {showImpersonateModal && impersonateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-2">Usurper l'identité de l'utilisateur</h3>
              <p className="text-sm text-gray-600 mb-4">
                Vous êtes sur le point d'usurper l'identité de <strong>{impersonateTarget.first_name} {impersonateTarget.last_name}</strong> ({impersonateTarget.email})
              </p>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Motif de l'usurpation *
                </label>
                <textarea
                  value={impersonateReason}
                  onChange={(e) => setImpersonateReason(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Saisissez le motif de l'usurpation..."
                />
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <Button variant="ghost" onClick={() => {
                  setShowImpersonateModal(false)
                  setImpersonateTarget(null)
                  setImpersonateReason('')
                }}>
                  Annuler
                </Button>
                <Button
                  variant="default"
                  onClick={handleImpersonate}
                  disabled={!impersonateReason.trim()}
                >
                  <LogIn className="h-4 w-4 mr-2" /> Usurper l'identité
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default UserList
